import i18n from './config'
import { SUPPORTED_LANGUAGES, SupportedLanguage, NAMESPACES, Namespace, LANGUAGE_DISPLAY_NAMES } from './constants'

/**
 * 多语言国际化工具函数
 */

/**
 * 安全获取翻译文本
 * @param key 翻译键
 * @param options 翻译选项
 * @returns 翻译后的文本，如果键不存在则返回键本身（开发环境）或空字符串（生产环境）
 */
export const safeTranslate = (key: string, options?: any): string => {
  if (!i18n.isInitialized) {
    return process.env.NODE_ENV === 'development' ? `[i18n未初始化] ${key}` : ''
  }

  if (!i18n.exists(key)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`缺失翻译键: ${key}`)
      return `[${key}]`
    }
    return key
  }

  return i18n.t(key, options) as string
}

/**
 * 检查翻译键是否存在
 */
export const hasTranslation = (key: string): boolean => {
  return i18n.exists(key)
}

/**
 * 获取当前语言
 */
export const getCurrentLanguage = (): SupportedLanguage => {
  return i18n.language as SupportedLanguage
}

/**
 * 切换语言
 * @param language 目标语言
 * @returns Promise<boolean> 是否切换成功
 */
export const changeLanguage = async (language: SupportedLanguage): Promise<boolean> => {
  try {
    await i18n.changeLanguage(language)
    return true
  } catch (error) {
    console.error('切换语言失败:', error)
    return false
  }
}

/**
 * 加载指定的命名空间
 * @param namespaces 要加载的命名空间
 */
export const loadNamespaces = async (namespaces: Namespace | Namespace[]): Promise<void> => {
  const nsArray = Array.isArray(namespaces) ? namespaces : [namespaces]
  await i18n.loadNamespaces(nsArray)
}

/**
 * 检查命名空间是否已加载
 */
export const hasNamespaceLoaded = (namespace: Namespace): boolean => {
  return i18n.hasLoadedNamespace(namespace)
}

/**
 * 获取所有已加载的命名空间
 */
export const getLoadedNamespaces = (): string[] => {
  return i18n.options.ns as string[]
}

/**
 * 格式化数字（根据当前语言）
 */
export const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
  const language = getCurrentLanguage()
  return new Intl.NumberFormat(language, options).format(num)
}

/**
 * 格式化货币（根据当前语言）
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'CNY',
  options?: Intl.NumberFormatOptions
): string => {
  const language = getCurrentLanguage()
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency,
    ...options,
  }).format(amount)
}

/**
 * 格式化日期（根据当前语言）
 */
export const formatDate = (
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions
): string => {
  const language = getCurrentLanguage()
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  return dateObj.toLocaleDateString(language, options)
}

/**
 * 格式化时间（根据当前语言）
 */
export const formatTime = (
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions
): string => {
  const language = getCurrentLanguage()
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  return dateObj.toLocaleTimeString(language, options)
}

/**
 * 获取相对时间描述（如"刚刚"、"2分钟前"等）
 */
export const formatRelativeTime = (
  date: Date | string | number,
  style: 'long' | 'short' | 'narrow' = 'short'
): string => {
  const language = getCurrentLanguage()
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  const rtf = new Intl.RelativeTimeFormat(language, { style })

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second')
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return rtf.format(-minutes, 'minute')
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return rtf.format(-hours, 'hour')
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400)
    return rtf.format(-days, 'day')
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000)
    return rtf.format(-months, 'month')
  } else {
    const years = Math.floor(diffInSeconds / 31536000)
    return rtf.format(-years, 'year')
  }
}

/**
 * 获取支持的语言列表
 */
export const getSupportedLanguages = () => {
  return Object.values(SUPPORTED_LANGUAGES).map((code) => ({
    code,
    name: LANGUAGE_DISPLAY_NAMES[code].english,
    nativeName: LANGUAGE_DISPLAY_NAMES[code].native,
  }))
}

/**
 * 验证翻译键格式
 */
export const validateTranslationKey = (key: string): boolean => {
  // 基本格式验证：至少包含一个点分隔符
  return key.includes('.') && key.split('.').length >= 2
}

/**
 * 提取翻译键的命名空间部分
 */
export const extractNamespaceFromKey = (key: string): string => {
  const parts = key.split('.')
  return parts[0]
}

/**
 * 提取翻译键的键名部分
 */
export const extractKeyName = (key: string): string => {
  const parts = key.split('.')
  return parts.slice(1).join('.')
}